import React from 'react';

export const WeatherIcon: React.FC<{ code: number }> = ({ code }) => {
    // Open-Meteo weather codes: https://open-meteo.com/en/docs
    // 0 clear, 1-3 cloudy, 45-48 fog, 51-57 drizzle, 61-67 rain, 71-77 snow,
    // 80-82 rain showers, 85-86 snow showers, 95-99 thunder.
    const kind:
        | 'sun'
        | 'cloudSun'
        | 'cloud'
        | 'fog'
        | 'rain'
        | 'snow'
        | 'thunder'
        | 'unknown' =
        code === 0
            ? 'sun'
            : code >= 1 && code <= 2
                ? 'cloudSun'
                : code === 3
                    ? 'cloud'
                    : (code >= 45 && code <= 48)
                        ? 'fog'
                        : (code >= 51 && code <= 67) || (code >= 80 && code <= 82)
                            ? 'rain'
                            : (code >= 71 && code <= 77) || (code >= 85 && code <= 86)
                                ? 'snow'
                                : code >= 95 && code <= 99
                                    ? 'thunder'
                                    : 'unknown';

    const common = {
        width: 28,
        height: 28,
        viewBox: '0 0 24 24',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
        className: 'weatherIconSvg',
    } as const;

    const stroke = 'currentColor';
    const strokeWidth = 1.8;
    const strokeLinecap = 'round' as const;
    const strokeLinejoin = 'round' as const;

    if (kind === 'sun') {
        return (
            <svg {...common}>
                <circle cx="12" cy="12" r="4" stroke={stroke} strokeWidth={strokeWidth} />
                <path d="M12 2v2" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M12 20v2" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M2 12h2" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M20 12h2" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M4.2 4.2l1.4 1.4" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M18.4 18.4l1.4 1.4" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M19.8 4.2l-1.4 1.4" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M5.6 18.4l-1.4 1.4" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
            </svg>
        );
    }

    if (kind === 'cloudSun') {
        return (
            <svg {...common}>
                <path d="M8 9a4 4 0 1 1 6.2 3.3" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} strokeLinejoin={strokeLinejoin} />
                <path
                    d="M8.5 19h9.2a3.3 3.3 0 0 0 .4-6.6 4.4 4.4 0 0 0-8.3 1.4A2.9 2.9 0 0 0 8.5 19Z"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                />
            </svg>
        );
    }

    if (kind === 'cloud') {
        return (
            <svg {...common}>
                <path
                    d="M7.8 19h9.6a3.4 3.4 0 0 0 .6-6.8 4.6 4.6 0 0 0-8.6 1.5A3 3 0 0 0 7.8 19Z"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                />
            </svg>
        );
    }

    if (kind === 'fog') {
        return (
            <svg {...common}>
                <path
                    d="M7.5 14.5h9a3 3 0 0 0 .3-6 4 4 0 0 0-7.6 1.3 2.6 2.6 0 0 0-2 2.7"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                />
                <path d="M5 17h14" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M6.5 20h11" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
            </svg>
        );
    }

    if (kind === 'rain') {
        return (
            <svg {...common}>
                <path
                    d="M7.8 15.5h9.6a3.4 3.4 0 0 0 .6-6.8 4.6 4.6 0 0 0-8.6 1.5A3 3 0 0 0 7.8 15.5Z"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                />
                <path d="M9 18l-1 2" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M13 18l-1 2" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
                <path d="M17 18l-1 2" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
            </svg>
        );
    }

    if (kind === 'snow') {
        return (
            <svg {...common}>
                <path
                    d="M7.8 15.5h9.6a3.4 3.4 0 0 0 .6-6.8 4.6 4.6 0 0 0-8.6 1.5A3 3 0 0 0 7.8 15.5Z"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                />
                <circle cx="9" cy="18" r="0.9" fill="currentColor" opacity="0.9" />
                <circle cx="13" cy="19.5" r="0.9" fill="currentColor" opacity="0.9" />
                <circle cx="17" cy="18" r="0.9" fill="currentColor" opacity="0.9" />
            </svg>
        );
    }

    if (kind === 'thunder') {
        return (
            <svg {...common}>
                <path
                    d="M7.8 15.5h9.6a3.4 3.4 0 0 0 .6-6.8 4.6 4.6 0 0 0-8.6 1.5A3 3 0 0 0 7.8 15.5Z"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeLinecap={strokeLinecap}
                    strokeLinejoin={strokeLinejoin}
                />
                <path d="M12 16.8l-2 3.7h2l-1 2.7 3-4.5h-2l1-1.9Z" fill="currentColor" opacity="0.9" />
            </svg>
        );
    }

    return (
        <svg {...common}>
            <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11Z" stroke={stroke} strokeWidth={strokeWidth} strokeLinejoin={strokeLinejoin} />
            <path d="M12 9v4" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
            <path d="M12 15h.01" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap={strokeLinecap} />
        </svg>
    );
};
